import { useEffect, useState } from "react";
import Card from "components/card";
import Navbar from "components/navbar";
import Loader from "components/loader/loader";
import { Switch } from "@chakra-ui/react";
import { t } from "i18next";
import { getAppConfig, createAppConfig, updateAppConfig, getAdminMercadoPagoOAuthUrlApi } from "services/customAPI";
import { errorToast, successToast } from "utils/commons";
import { Link } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { useOnboarding } from "hooks/useOnboarding";
import { OnboardingOverlay, OnboardingReplayButton } from "components/onboarding";
import { mercadoPagoOnboarding } from "components/onboarding/steps";
import {
    MdOutlineLink,
    MdOutlineSave,
    MdOutlinePercent,
    MdOutlineInfo,
    MdOutlineShield,
    MdOutlineAccountBalance,
    MdOutlineOpenInNew,
    MdOutlineCheckCircle,
    MdOutlineWarningAmber,
} from "react-icons/md";

const CONFIG_KEYS = ["SPLIT_PLATFORM_FEE_PERCENT", "ADMIN_MP_ACCESS_TOKEN"];

function MercadoPagoSettings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [linking, setLinking] = useState(false);
    const [configs, setConfigs] = useState<Record<string, string>>({});
    const [existingKeys, setExistingKeys] = useState<Set<string>>(new Set());
    const onboarding = useOnboarding(mercadoPagoOnboarding);

    useEffect(() => {
        const fetchConfigs = async () => {
            const tempConfigs: Record<string, string> = {};
            const tempExisting = new Set<string>();
            const results = await Promise.allSettled(
                CONFIG_KEYS.map(async (key) => {
                    const response = await getAppConfig(key);
                    return { key, response };
                })
            );
            for (const result of results) {
                if (result.status === "fulfilled") {
                    const { key, response } = result.value;
                    if (response?.data) {
                        tempConfigs[key] = response.data.value;
                        tempExisting.add(key);
                    } else {
                        tempConfigs[key] = "";
                    }
                }
            }
            CONFIG_KEYS.forEach((key) => { if (!(key in tempConfigs)) tempConfigs[key] = ""; });
            setConfigs(tempConfigs);
            setExistingKeys(tempExisting);
            setLoading(false);
        };
        fetchConfigs();
    }, []);

    const handleChange = (key: string, value: string) =>
        setConfigs((prev) => ({ ...prev, [key]: value }));

    const handleSave = async (showToast = true) => {
        setSaving(true);
        try {
            for (const key of CONFIG_KEYS) {
                if (key === "ADMIN_MP_ACCESS_TOKEN" && !configs[key]) continue;
                const configData = { name: key, value: configs[key] || "", placeHolder: `Config ${key}`, active: true };
                if (existingKeys.has(key)) {
                    await updateAppConfig(key, configData);
                } else {
                    await createAppConfig(configData);
                    setExistingKeys((prev) => new Set(prev).add(key));
                }
            }
            if (showToast) successToast("Configurações salvas com sucesso!");
        } catch (error: any) {
            if (showToast) errorToast("Erro ao salvar configurações.");
        } finally {
            setSaving(false);
        }
    };

    const handleLinkOAuth = async () => {
        await handleSave(false);
        setLinking(true);
        try {
            const response = await getAdminMercadoPagoOAuthUrlApi();
            if (response.data?.url) {
                window.location.href = response.data.url;
            } else {
                errorToast("Não foi possível gerar a URL de autorização.");
            }
        } catch (error: any) {
            const message = error?.response?.data?.message;
            errorToast(message || "As credenciais Master não estão configuradas corretamente.");
        } finally {
            setLinking(false);
        }
    };

    const isLinked = !!configs["ADMIN_MP_ACCESS_TOKEN"];
    const feeValue = configs["SPLIT_PLATFORM_FEE_PERCENT"] || "";
    const feeNum = parseFloat(feeValue || "0");

    return (
        <>
            <Navbar flag={false} brandText={t("Mercado Pago")} />
            <OnboardingOverlay
                isActive={onboarding.isActive}
                step={onboarding.currentStep}
                currentIndex={onboarding.currentIndex}
                totalSteps={onboarding.totalSteps}
                onNext={onboarding.next}
                onPrevious={onboarding.previous}
                onSkip={onboarding.skip}
            />
            <Link
                to="/admin/settings"
                className="mt-3 mb-1 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
                <FaArrowLeft className="h-3 w-3" />
                {t("Voltar para Configurações")}
            </Link>

            {loading ? (
                <Loader />
            ) : (
                <div className="mt-2 space-y-4 pb-8">

                    {/* ── Header Card ── */}
                    <Card className="p-6">
                        <header className="flex items-center justify-between">
                            <div>
                                <h1 className="text-2xl font-bold text-white">Mercado Pago</h1>
                                <p className="mt-1 text-sm text-gray-400">
                                    Integração Whitelabel — configure taxa e conta de recebimento
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <OnboardingReplayButton onReplay={onboarding.replay} />
                                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${feeValue ? "bg-green-500/20 text-green-400" : "bg-gray-600/30 text-gray-500"}`}>
                                    {feeValue ? `✓ Taxa configurada (${feeValue}%)` : "① Taxa pendente"}
                                </span>
                                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${isLinked ? "bg-green-500/20 text-green-400" : "bg-gray-600/30 text-gray-500"}`}>
                                    {isLinked ? "✓" : "2"} Conta vinculada
                                </span>
                            </div>
                        </header>
                    </Card>

                    {/* ── Two columns ── */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                        {/* ── Taxa da Plataforma ── */}
                        <Card className="p-6" id="mp-split-fee">
                            <div className="flex items-center gap-3 mb-5">
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-500/15 shrink-0">
                                    <MdOutlinePercent className="h-5 w-5 text-purple-400" />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold text-white">Taxa da Plataforma</h2>
                                    <p className="text-xs text-gray-500">% recolhido por corrida via Split</p>
                                </div>
                                {feeValue && (
                                    <div className="ml-auto">
                                        <span className="text-2xl font-bold text-green-400 bg-green-500/10 px-3 py-1 rounded-lg border border-green-500/20">
                                            {feeValue}%
                                        </span>
                                    </div>
                                )}
                            </div>

                            <label className="block text-xs text-gray-400 mb-1.5">Porcentagem (%)</label>
                            <div className="flex items-stretch">
                                <div className="flex items-center justify-center bg-white/10 border border-white/10 border-r-0 rounded-l-lg px-3">
                                    <span className="text-purple-400 font-bold text-sm">%</span>
                                </div>
                                <input
                                    type="number"
                                    placeholder="Ex: 10"
                                    value={feeValue}
                                    onChange={(e) => handleChange("SPLIT_PLATFORM_FEE_PERCENT", e.target.value)}
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    className="flex-1 bg-white/5 border border-white/10 rounded-r-lg px-4 py-3 text-white text-lg font-semibold placeholder:text-gray-600 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
                                />
                            </div>
                            <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-500">
                                <MdOutlineInfo className="h-3.5 w-3.5 shrink-0" />
                                <span>
                                    {feeValue
                                        ? `A plataforma reterá ${feeValue}% de cada corrida.`
                                        : "Defina o percentual retido pela plataforma em cada corrida."}
                                </span>
                            </div>

                            {/* Fee preview */}
                            {feeValue && feeNum > 0 && (
                                <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-4">
                                    <p className="text-xs text-gray-400 mb-3 font-medium">
                                        Prévia para corrida de R$ 100,00
                                    </p>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm text-gray-400">Motorista recebe</span>
                                        <span className="text-sm font-bold text-green-400">
                                            R$ {(100 - feeNum).toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="border-t border-white/10 my-1.5" />
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-400">Plataforma retém</span>
                                        <span className="text-sm font-bold text-purple-400">
                                            R$ {feeNum.toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </Card>

                        {/* ── Vinculação de Conta ── */}
                        <Card className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className={`flex h-10 w-10 items-center justify-center rounded-xl shrink-0 ${isLinked ? "bg-green-500/15" : "bg-amber-500/15"}`}>
                                        <MdOutlineAccountBalance className={`h-5 w-5 ${isLinked ? "text-green-400" : "text-amber-400"}`} />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-semibold text-white">Conta Admin</h2>
                                        <p className="text-xs text-gray-500">Conta que recebe a taxa da plataforma</p>
                                    </div>
                                </div>
                                <span className={`text-xs font-bold uppercase px-2.5 py-1 rounded-full ${isLinked ? "bg-green-500/20 text-green-400" : "bg-amber-500/20 text-amber-400"}`}>
                                    {isLinked ? "Vinculada" : "Não vinculada"}
                                </span>
                            </div>

                            <div className={`rounded-xl p-4 mb-4 flex items-start gap-2 ${isLinked ? "bg-green-500/10 border border-green-500/20" : "bg-amber-500/10 border border-amber-500/20"}`}>
                                {isLinked
                                    ? <MdOutlineCheckCircle className="h-4 w-4 text-green-400 shrink-0 mt-0.5" />
                                    : <MdOutlineWarningAmber className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                                }
                                <span className={`text-sm ${isLinked ? "text-green-300/90" : "text-amber-300/90"}`}>
                                    {isLinked
                                        ? "Conta Mercado Pago vinculada com sucesso. A taxa será transferida automaticamente."
                                        : "Vincule sua conta para começar a receber a taxa da plataforma via split automático."}
                                </span>
                            </div>

                            <button
                                id="mp-oauth-btn"
                                onClick={handleLinkOAuth}
                                disabled={linking || saving}
                                className={`w-full flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold transition-colors disabled:opacity-50 ${isLinked
                                    ? "border border-green-500/40 text-green-400 hover:bg-green-500/10"
                                    : "bg-amber-600 text-white hover:bg-amber-700"
                                    }`}
                            >
                                {linking || saving ? (
                                    <div className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                                ) : isLinked ? (
                                    <MdOutlineOpenInNew className="h-4 w-4" />
                                ) : (
                                    <MdOutlineLink className="h-4 w-4" />
                                )}
                                {isLinked ? "Renovar Vinculação OAuth" : "Vincular Minha Conta Mercado Pago"}
                            </button>

                            <div className="flex items-center gap-1.5 mt-3 text-xs text-gray-500">
                                <MdOutlineShield className="h-3.5 w-3.5 shrink-0" />
                                <span>Redirecionamento seguro via OAuth 2.0.</span>
                            </div>
                        </Card>
                    </div>

                    {/* ── Info ── */}
                    <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl px-4 py-3 flex items-start gap-2">
                        <span className="text-cyan-400 mt-0.5">ⓘ</span>
                        <p className="text-sm text-cyan-300/80">
                            <strong>Como funciona:</strong>{" "}
                            Ao concluir uma corrida, o Mercado Pago divide o valor automaticamente —
                            a porcentagem configurada vai direto para a conta do admin e o restante para o motorista.
                        </p>
                    </div>

                    {/* ── Save ── */}
                    <div className="flex justify-end">
                        <button
                            id="mp-save-btn"
                            onClick={() => handleSave(true)}
                            disabled={saving}
                            className="flex items-center gap-2 rounded-lg bg-cyan-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-cyan-700 transition-colors disabled:opacity-50"
                        >
                            {saving ? (
                                <div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                            ) : (
                                <MdOutlineSave className="h-4 w-4" />
                            )}
                            Salvar Configurações
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

export default MercadoPagoSettings;
